import React, { useState, useEffect } from "react";
import api from "../../api/client";
import {
  Typography,
  Box,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  AccountTreeRounded as DbIcon,
  TableChartRounded as TableIcon,
  CloseRounded as CloseIcon,
  StorageRounded as StorageIcon,
} from "@mui/icons-material";

export default function SqlExplorerModal({ open, onClose, server }) {
  const [dbs, setDbs] = useState([]);
  const [tables, setTables] = useState([]);
  const [schema, setSchema] = useState([]);
  const [activeDb, setActiveDb] = useState(null);
  const [activeTable, setActiveTable] = useState(null);
  const [sqlLoading, setSqlLoading] = useState(false);

  // Modal açıldığında veritabanlarını çek
  useEffect(() => {
    if (open && server) fetchDbs();
    // eslint-disable-next-line
  }, [open]);

  const fetchDbs = async () => {
    setSqlLoading(true);
    try {
      const res = await api.post("/mysql-query", {
        serverId: server.id,
        query: "SHOW DATABASES;",
      });
      setDbs(res.data.data.map((r) => Object.values(r)[0]));
    } catch (e) {
      alert(
        "Veritabanları alınamadı. MySQL bilgileri hatalı veya servis kapalı olabilir.",
      );
    }
    setSqlLoading(false);
  };

  const fetchTables = async (dbName) => {
    setActiveDb(dbName);
    setTables([]);
    setSchema([]);
    setActiveTable(null);
    setSqlLoading(true);
    try {
      const res = await api.post("/mysql-query", {
        serverId: server.id,
        currentDb: dbName,
        query: "SHOW TABLES;",
      });
      setTables(res.data.data.map((r) => Object.values(r)[0]));
    } catch (e) {}
    setSqlLoading(false);
  };

  const fetchSchema = async (tableName) => {
    setActiveTable(tableName);
    setSchema([]);
    setSqlLoading(true);
    try {
      const res = await api.post("/mysql-query", {
        serverId: server.id,
        currentDb: activeDb,
        query: `DESCRIBE ${tableName};`,
      });
      setSchema(res.data.data);
    } catch (e) {}
    setSqlLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#0a0a0d",
          borderRadius: 4,
          border: "1px solid rgba(234,240,248,0.1)",
          minHeight: "60vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(234,240,248,0.05)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <DbIcon color="secondary" />
          <Typography variant="h6" fontWeight="bold">
            Visione Data Explorer / {server?.name}
          </Typography>
          {sqlLoading && <CircularProgress size={20} color="secondary" />}
        </Stack>
        <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ height: "100%", minHeight: "500px" }}>
          {/* Sütun 1: DB'ler */}
          <Grid size={{ xs: 12, md: 3 }} sx={{ borderRight: "1px solid rgba(234,240,248,0.05)", p: 2 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight="bold"
            >
              Veritabanları
            </Typography>
            <Stack spacing={1} mt={1}>
              {dbs.map((db, i) => (
                <Button
                  key={i}
                  fullWidth
                  variant={activeDb === db ? "contained" : "text"}
                  color="secondary"
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                  onClick={() => fetchTables(db)}
                >
                  <StorageIcon sx={{ mr: 1, fontSize: 18 }} /> {db}
                </Button>
              ))}
            </Stack>
          </Grid>
          {/* Sütun 2: Tablolar */}
          <Grid size={{ xs: 12, md: 3 }} sx={{ borderRight: "1px solid rgba(234,240,248,0.05)", p: 2, bgcolor: "rgba(0,0,0,0.2)", }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight="bold"
            >
              Tablolar {activeDb && `(${activeDb})`}
            </Typography>
            <Stack spacing={1} mt={1}>
              {tables.length === 0 && !sqlLoading && (
                <Typography variant="caption" color="text.disabled">
                  Tablo bulunamadı.
                </Typography>
              )}
              {tables.map((tbl, i) => (
                <Button
                  key={i}
                  fullWidth
                  variant={activeTable === tbl ? "contained" : "text"}
                  color="primary"
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                  onClick={() => fetchSchema(tbl)}
                >
                  <TableIcon sx={{ mr: 1, fontSize: 18 }} /> {tbl}
                </Button>
              ))}
            </Stack>
          </Grid>
          {/* Sütun 3: Schema */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ p: 2, bgcolor: "#030305" }}>
            <Typography
              variant="overline"
              color="primary.light"
              fontWeight="bold"
            >
              Tablo Yapısı {activeTable && `(${activeTable})`}
            </Typography>
            <Box mt={1}>
              {schema.length === 0 && !sqlLoading && (
                <Typography variant="caption" color="text.disabled">
                  İncelemek için bir tablo seçin.
                </Typography>
              )}
              {schema.length > 0 && (
                <TableContainer
                  sx={{
                    bgcolor: "rgba(234,240,248,0.02)",
                    borderRadius: 2,
                    border: "1px solid rgba(234,240,248,0.05)",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "rgba(234,240,248,0.05)" }}>
                        <TableCell
                          sx={{ color: "text.primary", fontWeight: "bold" }}
                        >
                          Field
                        </TableCell>
                        <TableCell
                          sx={{ color: "text.primary", fontWeight: "bold" }}
                        >
                          Type
                        </TableCell>
                        <TableCell
                          sx={{ color: "text.primary", fontWeight: "bold" }}
                        >
                          Null
                        </TableCell>
                        <TableCell
                          sx={{ color: "text.primary", fontWeight: "bold" }}
                        >
                          Key
                        </TableCell>
                        <TableCell
                          sx={{ color: "text.primary", fontWeight: "bold" }}
                        >
                          Extra
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {schema.map((col, i) => (
                        <TableRow
                          key={i}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell
                            sx={{
                              color: "secondary.main",
                              fontFamily: "monospace",
                            }}
                          >
                            {col.Field}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "text.secondary",
                              fontFamily: "monospace",
                            }}
                          >
                            {col.Type}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "text.secondary",
                              fontFamily: "monospace",
                            }}
                          >
                            {col.Null}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "warning.main",
                              fontFamily: "monospace",
                              fontWeight: "bold",
                            }}
                          >
                            {col.Key}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "text.disabled",
                              fontFamily: "monospace",
                            }}
                          >
                            {col.Extra}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
